/**
 * Fold meeting realtime events into the TanStack cache.
 *
 * NOTHING HERE REFETCHES. Every event carries the whole object it changed, so a
 * poll that was voted on, a question that was answered and a note that was
 * saved all land as a cache write — which is the difference between a panel
 * that updates and a panel that updates a second later, for everyone, at once.
 *
 * The one thing it must not do is duplicate the CALLER's own write: the sender
 * of a chat line already has it, reconciled by client id, so the echo is
 * dropped by message id here rather than appended a second time.
 */
import type { QueryClient } from '@tanstack/react-query';
import type { Meeting } from '@cyphex/types/meetings';
import type { RealtimeEvent } from '@cyphex/types/realtime';
import type {
  BreakoutRoomDto,
  MeetingChatMessageDto,
  MeetingChatPageDto,
  MeetingDto,
  MeetingNotesDto,
  MeetingParticipantDto,
  PollDto,
  QuestionDto,
} from '../data/contracts';
import { lobbyEntryToDto } from '../data/meetingMappers';
import { MEETING_KEYS } from '../data/queryKeys';

export function applyEventToMeeting(client: QueryClient, event: RealtimeEvent): void {
  switch (event.type) {
    case 'meeting.chat.new': {
      const key = MEETING_KEYS.chat(event.meetingId);
      client.setQueryData<MeetingChatPageDto>(key, (page) => {
        if (page === undefined) return page;
        const incoming = event.message as unknown as MeetingChatMessageDto;
        if (page.messages.some((message) => message.id === incoming.id)) return page;
        return { ...page, messages: [...page.messages, incoming] };
      });
      return;
    }

    case 'meeting.poll.updated':
    case 'meeting.poll.closed': {
      const key = MEETING_KEYS.polls(event.meetingId);
      client.setQueryData<readonly PollDto[]>(key, (polls) => {
        const incoming = event.poll as unknown as PollDto;
        if (polls === undefined) return [incoming];
        const known = polls.some((poll) => poll.id === incoming.id);
        // The broadcast copy carries nobody's `myVotes` — keep the caller's own
        // selection rather than letting somebody else's vote erase it.
        return known
          ? polls.map((poll) =>
              poll.id === incoming.id ? { ...incoming, myVotes: poll.myVotes } : poll,
            )
          : [incoming, ...polls];
      });
      return;
    }

    case 'meeting.qa.asked':
    case 'meeting.qa.answered':
    case 'meeting.qa.updated': {
      const key = MEETING_KEYS.questions(event.meetingId);
      client.setQueryData<readonly QuestionDto[]>(key, (questions) => {
        const incoming = event.question as unknown as QuestionDto;
        const rest = (questions ?? []).filter((question) => question.id !== incoming.id);
        if (incoming.status === 'dismissed') return rest;
        const previous = (questions ?? []).find((question) => question.id === incoming.id);
        // Same rule: `hasUpvoted` in the broadcast is not this reader's.
        const merged =
          previous === undefined ? incoming : { ...incoming, hasUpvoted: previous.hasUpvoted };
        return [merged, ...rest].sort(
          (a, b) => b.upvoteCount - a.upvoteCount || b.createdAt.localeCompare(a.createdAt),
        );
      });
      return;
    }

    case 'meeting.notes.updated': {
      client.setQueryData<MeetingNotesDto>(
        MEETING_KEYS.notes(event.meetingId),
        event.notes as unknown as MeetingNotesDto,
      );
      return;
    }

    case 'meeting.breakout.opened':
    case 'meeting.breakout.closed':
    case 'meeting.breakout.assigned': {
      client.setQueryData<readonly BreakoutRoomDto[]>(
        MEETING_KEYS.breakouts(event.meetingId),
        event.rooms as unknown as BreakoutRoomDto[],
      );
      return;
    }

    /*
     * SOMEBODY IS AT THE DOOR - or no longer is.
     *
     * The server has always announced these and nothing here listened: the
     * host's queue was one GET, made when the People panel happened to be
     * opened, and never refreshed. Folded into the cache that panel reads, the
     * queue is live whether or not the panel is on screen - which is what lets
     * the host be TOLD (hooks/useLobbyAlerts.ts).
     */
    case 'meeting.lobby.waiting': {
      client.setQueryData<readonly MeetingParticipantDto[]>(
        MEETING_KEYS.waiting(event.meetingId),
        (queue) => {
          const known = new Set((queue ?? []).map((person) => person.id));
          const fresh = event.entries
            .map(lobbyEntryToDto)
            .filter((person) => !known.has(person.id));
          return fresh.length === 0 ? queue : [...(queue ?? []), ...fresh];
        },
      );
      return;
    }

    case 'meeting.lobby.admitted':
    case 'meeting.lobby.denied': {
      const decided = new Set(event.entries.map((entry) => entry.participantId));
      client.setQueryData<readonly MeetingParticipantDto[]>(
        MEETING_KEYS.waiting(event.meetingId),
        (queue) => (queue === undefined ? queue : queue.filter((person) => !decided.has(person.id))),
      );
      return;
    }

    case 'meeting.updated': {
      // The host toggles live on the meeting object: chat, reactions, raise
      // hand and Q&A all change what the panels offer, so the detail cache is
      // updated in place instead of waiting for a stale-time to expire.
      client.setQueryData(MEETING_KEYS.detail(event.meetingId), (current: unknown) =>
        current === undefined ? current : { ...(current as object), ...toMeetingPatch(event.meeting) },
      );
      return;
    }

    default:
      return;
  }
}

/** Only the fields the meeting DTO shares with the wire `Meeting`. */
function toMeetingPatch(meeting: Meeting): Partial<MeetingDto> {
  return {
    status: meeting.status,
    allowChat: meeting.allowChat,
    allowReactions: meeting.allowReactions,
    allowRaiseHand: meeting.allowRaiseHand,
    allowQa: meeting.allowQa,
    allowAttendeeUnmute: meeting.allowAttendeeUnmute,
    allowAttendeeVideo: meeting.allowAttendeeVideo,
    allowAttendeeShare: meeting.allowAttendeeShare,
    isLocked: meeting.isLocked,
    muteOnEntry: meeting.muteOnEntry,
    cameraOffOnEntry: meeting.cameraOffOnEntry,
    lobbyPolicy: meeting.lobbyPolicy,
    presenterPolicy: meeting.presenterPolicy,
  };
}
